/* eslint-disable jsx-a11y/control-has-associated-label */
import type { SaveStockAdjustmentRequest } from '@/renderer/types/inventory';
import { apiError, apiSuccess } from '@/renderer/util/notify';
import {
  Button,
  ConfirmDialog,
  DatePicker,
  DropdownSelect,
  EmptyState,
} from '@components/common';
import { FormTextarea } from '@components/common/FormField';
import {
  useInventoryItems,
  useInventoryStockBalances,
  useInventoryStockLocations,
  usePostStockAdjustment,
  useSaveStockAdjustment,
} from '@hooks/inventory';
import { useAppSelector } from '@hooks/useRedux';
import { selectCurrentUser } from '@store/auth/authSelectors';
import { selectEffectiveOutletId } from '@store/outlets';
import { ClipboardList, Download, RefreshCw, Upload } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { exportToCsv } from '../../accommodation/reports/reportUtils';

interface CountLine {
  inventoryItemId: string;
  itemName: string;
  expectedQty: number;
  countedQty: string;
}

interface FormState {
  stockLocationId: string;
  countDate: string;
  notes: string;
}

const today = () => new Date().toISOString().split('T')[0];

const initialForm = (): FormState => ({
  stockLocationId: '',
  countDate: today(),
  notes: '',
});

const fmt = (n: number) =>
  n.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export const StockCountPage: React.FC = () => {
  const currentUser = useAppSelector(selectCurrentUser);
  const companyId = currentUser?.company?.id ?? null;
  const outletId = useAppSelector(selectEffectiveOutletId);

  const [form, setForm] = useState<FormState>(initialForm);
  const [countLines, setCountLines] = useState<CountLine[]>([]);
  const [sheetGenerated, setSheetGenerated] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingPost, setPendingPost] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const locationsQuery = useInventoryStockLocations({
    companyId: companyId ?? 0,
    outletId: outletId ?? '',
    page: 1,
    pageSize: 200,
  });
  const balancesQuery = useInventoryStockBalances({
    companyId: companyId ?? 0,
    outletId: outletId ?? '',
    stockLocationId: form.stockLocationId || undefined,
    page: 1,
    pageSize: 500,
  });
  const itemsQuery = useInventoryItems({
    companyId: companyId ?? 0,
    page: 1,
    pageSize: 500,
    status: 'active',
  });

  const { mutateAsync: saveAdjustment, isPending: isSaving } =
    useSaveStockAdjustment();
  const { mutateAsync: postAdjustment, isPending: isPosting } =
    usePostStockAdjustment();

  const locations = locationsQuery.data?.data ?? [];
  const items = itemsQuery.data?.data ?? [];

  // Reset sheet when location changes
  useEffect(() => {
    setSheetGenerated(false);
    setCountLines([]);
  }, [form.stockLocationId]);

  const generateSheet = () => {
    if (!form.stockLocationId) return;
    const balances = balancesQuery.data?.data ?? [];
    const balanceMap = new Map(
      balances.map((b) => [b.inventory_item_id, b.quantity_on_hand]),
    );

    const lines: CountLine[] = items
      .filter(
        (item) =>
          balanceMap.has(item.id) || balanceMap.get(item.id) !== undefined,
      )
      .map((item) => ({
        inventoryItemId: item.id,
        itemName: item.name + (item.sku ? ` (${item.sku})` : ''),
        expectedQty: balanceMap.get(item.id) ?? 0,
        countedQty: '',
      }))
      .filter((l) => l.expectedQty > 0);

    // Also include items from balance list not already added
    // eslint-disable-next-line no-restricted-syntax
    for (const balance of balances) {
      if (!lines.find((l) => l.inventoryItemId === balance.inventory_item_id)) {
        const item = items.find((i) => i.id === balance.inventory_item_id);
        lines.push({
          inventoryItemId: balance.inventory_item_id,
          itemName: item
            ? item.name + (item.sku ? ` (${item.sku})` : '')
            : balance.inventory_item_id,
          expectedQty: balance.quantity_on_hand,
          countedQty: '',
        });
      }
    }

    setCountLines(lines.sort((a, b) => a.itemName.localeCompare(b.itemName)));
    setSheetGenerated(true);
  };

  const setCounted = (inventoryItemId: string, value: string) => {
    setCountLines((lines) =>
      lines.map((l) =>
        l.inventoryItemId === inventoryItemId ? { ...l, countedQty: value } : l,
      ),
    );
  };

  const variances = countLines.filter((l) => {
    const counted = Number(l.countedQty);
    return l.countedQty !== '' && counted !== l.expectedQty;
  });

  const exportSheet = () => {
    exportToCsv(
      'stock-count-sheet',
      countLines.map((l) => ({
        'Item Name': l.itemName,
        'System Qty': l.expectedQty,
        'Counted Qty': l.countedQty !== '' ? Number(l.countedQty) : '',
        Variance:
          l.countedQty !== '' ? Number(l.countedQty) - l.expectedQty : '',
      })),
    );
  };

  const handleUploadCsv = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target?.result as string;
      if (!text) return;
      const lines = text.split('\n').filter(Boolean);
      if (lines.length < 2) return;
      const headers = lines[0]
        .split(',')
        .map((h) => h.replace(/^"|"$/g, '').trim());
      const nameIdx = headers.findIndex((h) => /item.?name/i.test(h));
      const qtyIdx = headers.findIndex((h) => /counted.?qty/i.test(h));
      if (nameIdx === -1 || qtyIdx === -1) {
        apiError('CSV must have "Item Name" and "Counted Qty" columns');
        return;
      }
      let matched = 0;
      let unmatched = 0;
      setCountLines((prev) => {
        const updated = [...prev];
        for (let i = 1; i < lines.length; i += 1) {
          const cols = lines[i]
            .split(',')
            .map((c) => c.replace(/^"|"$/g, '').trim());
          const name = cols[nameIdx]?.trim();
          const qty = cols[qtyIdx]?.trim();
          // eslint-disable-next-line no-continue
          if (!name || qty === '' || qty === undefined) continue;
          const idx = updated.findIndex((l) => l.itemName === name);
          if (idx !== -1) {
            updated[idx] = { ...updated[idx], countedQty: qty };
            matched += 1;
          } else {
            unmatched += 1;
          }
        }
        return updated;
      });
      setTimeout(() => {
        if (unmatched > 0) {
          apiError(
            `Imported ${matched} row(s); ${unmatched} row(s) not matched`,
          );
        } else {
          apiSuccess(`Imported ${matched} counted quantities from CSV`);
        }
      }, 50);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleSubmit = async () => {
    if (!companyId || !outletId || !form.stockLocationId) return;

    const varianceLines = countLines.filter((l) => {
      const counted = Number(l.countedQty);
      return (
        l.countedQty !== '' &&
        !Number.isNaN(counted) &&
        counted !== l.expectedQty
      );
    });

    if (varianceLines.length === 0) {
      apiError('No variances to post — all counts match system quantities');
      return;
    }

    const request: SaveStockAdjustmentRequest = {
      companyId,
      outletId,
      stockLocationId: form.stockLocationId,
      adjustmentType: 'stock_count',
      adjustmentDate: form.countDate,
      reference: `Stock count ${form.countDate}`,
      notes:
        form.notes || `Physical stock count — ${variances.length} variance(s)`,
      lines: varianceLines.map((l) => ({
        inventoryItemId: l.inventoryItemId,
        quantityAdjusted: Number(l.countedQty) - l.expectedQty,
        unitCost: null,
        reason: 'Physical stock count correction',
      })),
    };

    try {
      const result = await saveAdjustment(request);
      if (!result.success || !result.data?.id) {
        apiError(result.error ?? 'Failed to save stock count');
        return;
      }
      setPendingPost(result.data.id);
      setShowConfirm(true);
    } catch {
      apiError('Failed to save stock count');
    }
  };

  const handlePost = async () => {
    if (!pendingPost) return;
    setShowConfirm(false);
    try {
      await postAdjustment(pendingPost);
      apiSuccess(
        `Stock count posted — ${variances.length} variance(s) corrected`,
      );
      setForm(initialForm());
      setCountLines([]);
      setSheetGenerated(false);
      setPendingPost(null);
    } catch {
      apiError('Failed to post stock count');
    }
  };

  if (!companyId || !outletId) {
    return (
      <EmptyState
        title="Context required"
        description="Select an outlet to run a stock count."
      />
    );
  }

  const busy = isSaving || isPosting;

  return (
    <>
      <div className="space-y-6">
        <div className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          <ClipboardList size={20} className="text-pos-primary" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Physical stock count
            </h2>
            <p className="text-sm text-gray-500">
              Generate a count sheet for a location, enter actual quantities,
              then post corrections for any variances.
            </p>
          </div>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <h3 className="mb-4 text-sm font-semibold text-gray-700">
            Count setup
          </h3>
          <div className="grid grid-cols-3 gap-4">
            <DropdownSelect
              label="Stock location"
              value={form.stockLocationId}
              placeholder="Select location"
              options={locations.map((l) => ({ value: l.id, label: l.name }))}
              onChange={(v) => setForm((f) => ({ ...f, stockLocationId: v }))}
            />
            <DatePicker
              label="Count date"
              mode="single"
              value={form.countDate}
              onChange={(v) => setForm((f) => ({ ...f, countDate: v }))}
            />
            <FormTextarea
              label="Notes"
              value={form.notes}
              onChange={(e) =>
                setForm((f) => ({ ...f, notes: e.target.value }))
              }
              rows={1}
              placeholder="Optional count notes"
            />
          </div>
          <div className="mt-4 flex gap-3">
            <Button
              onClick={generateSheet}
              disabled={!form.stockLocationId || balancesQuery.isFetching}
              isLoading={balancesQuery.isFetching}
            >
              <RefreshCw size={14} />
              Generate count sheet
            </Button>
            {sheetGenerated && (
              <span className="flex items-center text-sm text-gray-500">
                {countLines.length} items loaded
              </span>
            )}
          </div>
        </div>

        {sheetGenerated && countLines.length === 0 && (
          <EmptyState
            title="No stock on hand"
            description="This location has no items with stock on hand to count."
          />
        )}

        {sheetGenerated && countLines.length > 0 && (
          <>
            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
              <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
                <div>
                  <h3 className="text-sm font-semibold text-gray-800">
                    Count sheet
                  </h3>
                  <p className="text-xs text-gray-400">
                    Enter actual counted quantities. Leave blank to skip an
                    item.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {variances.length > 0 && (
                    <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-700">
                      {variances.length} variance
                      {variances.length !== 1 ? 's' : ''}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={exportSheet}
                    className="flex items-center gap-1.5 rounded-xl border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <Download size={12} />
                    Export sheet
                  </button>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-1.5 rounded-xl border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <Upload size={12} />
                    Upload CSV
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    className="hidden"
                    onChange={handleUploadCsv}
                  />
                </div>
              </div>
              <div className="h-[32rem] overflow-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 border-b border-gray-100 bg-gray-50">
                    <tr className="text-left">
                      <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Item
                      </th>
                      <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                        System qty
                      </th>
                      <th className="w-36 px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Counted qty
                      </th>
                      <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Variance
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {countLines.map((line) => {
                      const counted = Number(line.countedQty);
                      const hasCount =
                        line.countedQty !== '' && !Number.isNaN(counted);
                      const variance = hasCount
                        ? counted - line.expectedQty
                        : null;
                      const isVariance = variance !== null && variance !== 0;
                      return (
                        <tr
                          key={line.inventoryItemId}
                          className={`border-t border-gray-100 ${isVariance ? 'bg-amber-50/40' : ''}`}
                        >
                          <td className="px-5 py-2 font-medium text-gray-900">
                            {line.itemName}
                          </td>
                          <td className="px-5 py-2 text-right font-mono text-gray-600">
                            {fmt(line.expectedQty)}
                          </td>
                          <td className="px-5 py-2">
                            <input
                              type="number"
                              min="0"
                              step="0.0001"
                              value={line.countedQty}
                              onChange={(e) =>
                                setCounted(line.inventoryItemId, e.target.value)
                              }
                              placeholder="—"
                              className="w-full rounded-lg border border-gray-200 px-2.5 py-1.5 text-right text-sm focus:border-pos-primary focus:outline-none focus:ring-1 focus:ring-pos-primary/30"
                            />
                          </td>
                          <td
                            className={`px-5 py-2 text-right font-mono font-semibold ${
                              variance === null
                                ? 'text-gray-300'
                                : variance > 0
                                  ? 'text-emerald-600'
                                  : variance < 0
                                    ? 'text-red-600'
                                    : 'text-gray-400'
                            }`}
                          >
                            {variance === null
                              ? '—'
                              : variance === 0
                                ? '✓'
                                : `${variance > 0 ? '+' : ''}${fmt(variance)}`}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setCountLines((lines) =>
                    lines.map((l) => ({ ...l, countedQty: '' })),
                  );
                  setShowReview(false);
                }}
                disabled={busy}
              >
                Clear entries
              </Button>
              <Button
                onClick={() => setShowReview(true)}
                disabled={variances.length === 0 || busy}
              >
                Review {variances.length} variance
                {variances.length !== 1 ? 's' : ''}
              </Button>
            </div>

            {showReview && variances.length > 0 && (
              <div className="overflow-hidden rounded-2xl border border-amber-200 bg-amber-50">
                <div className="flex items-center justify-between border-b border-amber-200 px-5 py-3">
                  <div>
                    <h3 className="text-sm font-semibold text-amber-900">
                      Variance review
                    </h3>
                    <p className="text-xs text-amber-700">
                      {
                        variances.filter(
                          (v) => Number(v.countedQty) > v.expectedQty,
                        ).length
                      }{' '}
                      over,{' '}
                      {
                        variances.filter(
                          (v) => Number(v.countedQty) < v.expectedQty,
                        ).length
                      }{' '}
                      under &mdash; {variances.length} total
                    </p>
                  </div>
                  <Button onClick={handleSubmit} isLoading={busy}>
                    Post corrections
                  </Button>
                </div>
                <div className="max-h-[24rem] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 border-b border-amber-200 bg-amber-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-amber-700">
                          Item
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-semibold text-amber-700">
                          System Qty
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-semibold text-amber-700">
                          Counted Qty
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-semibold text-amber-700">
                          Variance
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-amber-100">
                      {variances.map((v) => {
                        const variance = Number(v.countedQty) - v.expectedQty;
                        return (
                          <tr
                            key={v.inventoryItemId}
                            className="hover:bg-amber-100/50"
                          >
                            <td className="px-4 py-2 font-medium text-gray-900">
                              {v.itemName}
                            </td>
                            <td className="px-4 py-2 text-right font-mono text-gray-600">
                              {fmt(v.expectedQty)}
                            </td>
                            <td className="px-4 py-2 text-right font-mono text-gray-900">
                              {fmt(Number(v.countedQty))}
                            </td>
                            <td
                              className={`px-4 py-2 text-right font-mono font-semibold ${variance > 0 ? 'text-emerald-600' : 'text-red-600'}`}
                            >
                              {variance > 0 ? '+' : ''}
                              {fmt(variance)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <ConfirmDialog
        isOpen={showConfirm}
        title="Post stock count corrections?"
        message={`This will apply ${variances.length} correction(s) to stock quantities. This cannot be undone.`}
        confirmText="Post corrections"
        onConfirm={handlePost}
        onClose={() => {
          setShowConfirm(false);
          setPendingPost(null);
        }}
      />
    </>
  );
};
